import { Injectable, ErrorHandler }    from '@angular/core';
import * as Rx from 'rxjs';
import { Notifier } from '@airbrake/browser'
import { airbrakeProjectID, airbrakeKey, isProduction } from '../app.config';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

export enum ErrorObjectTypeEnum {
    Report,
    Table,
    Graph
}

/**
 * Special type of Error to wrap an error loading a component.
 */
export class LoadingError extends Error {

    constructor(componentName?: string, errorObjectType?: ErrorObjectTypeEnum) {
        let error: string;

        switch(errorObjectType){
            case ErrorObjectTypeEnum.Report:
                error = 'The "' + componentName + '" report has failed to load. Please try again.'
                break;
            case ErrorObjectTypeEnum.Table:
                error = 'The "' + componentName + '" table has failed to load. Please try again.'
                break;
            case ErrorObjectTypeEnum.Graph:
            default:
                error = 'The "' + componentName + '" graph has failed to load. Please try again.'
                break;
        }

        super(error);
    }
}

/**
 * Service that lets something subscribe to be notified of any unhandled Errors.
 */
@Injectable()
export class ErrorCatchingService extends ErrorHandler {

    airbrake = new Notifier({
        projectId: airbrakeProjectID(),
        projectKey: airbrakeKey(),
        environment: isProduction ? 'production' : 'development'
    });

    /** Observable stream of caught errors */
    public readonly error$ = new Rx.Subject<Error>();

    /** Construct */
    constructor() {
        //super(true);
        super();
    }

    /**
     * Receive an unhandled error.
     * @param error
     * @override
     */
    handleError(error: any) {

        // Translate into a simple Error
        let originalError = this.ensureIsError(this.findOriginalError(error));

        // Notify asynchronously, so that it can't delay the super impl.
        setTimeout(() => this.error$.next(originalError));

        // Super will print to the console and rethrow.
        super.handleError(error);
        this.airbrake.notify(error);
    }

    /**
     * Dig down through Error chain created by async and Promise to find the original Error.
     */
    private findOriginalError(error: any): any {
        while (error) {
            if (error.originalError) //nested async
                error = error.originalError;
            else if (error.rejection) //Promise rejection
                error = error.rejection;
            else
                break;
        }

        return error;
    }

    /**
     * Take any thrown thing and return an Error instance.
     * @param thrown
     * @returns {Error}
     */
    private ensureIsError(thrown: any): Error {
        if (thrown instanceof Error) {
            return thrown;
        }
        else if (thrown.name && thrown.message) {
            return thrown as Error;
        }
        else if (thrown instanceof HttpResponse) {
            let response = thrown as HttpResponse<JSON>;
            let jsonStr = response.body;

            let map = this.objToStrMap(jsonStr)
            let e = new HttpErrorResponse({
                status: response.status,
                statusText: response.statusText
            });
            return e;
        }
        else {
            return new Error(thrown.toString());
        }
    }

    private objToStrMap(obj) {
        let strMap = new Map();
        for (let k of Object.keys(obj)) {
            strMap.set(k, obj[k]);
        }
        return strMap;
    }
}
